import _ from "lodash";
import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
/** internal */
import { fontStyle } from "Constants";
import QuestionFieldComponent from "Worksheet/components/Question/QuestionFieldComponent";
import {
  getIsFieldHidden,
  getFieldType,
} from "Worksheet/modules/WorksheetUtils";
import { getIsLearningGoalsRevampEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import {
  FIELD_UID_ASSESSMENT_TOOL,
  RESOURCE_TYPE_QUESTION,
  MODE_TEACHER_CREATE,
  FIELD_UID_ADD_LEARNING_GOALS,
} from "Worksheet/modules/WorksheetConstants";
import { getSubFields } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
/** relative */
import classes from "./DynamicFieldComponent.scss";
import QuestionFieldLabel from "../../../QuestionFieldLabel";

const styles = {
  customTextStyle: {
    ...fontStyle.medium,
    fontSize: "1.4rem",
    lineHeight: 1.71,
    fontWeight: "400",
  },
};
const DynamicFieldComponent = props => {
  const {
    fieldUid,
    getExtraRightButtonComponent,
    data,
    isActiveQuestion,
    questionId,
    assessmentTools,
    mode,
    didMount,
    setDidMount,
    showDynamicFields,
    isSubQuestionSelected,
    isFromInternalTool,
    portalType,
    learningGoalsDropdownOptions,
    selectedCourseId,
    learningGoalsFields,
    assessmentType,
    worksheetTemplateId,
    unitPlanFields,
    unitPlanFieldList,
    learningGoalsMapping,
    getLearningGoalsTagCountTooltip,
    dynamicFieldDetails,
    setDynamicFieldRef,
    addedLearningGoalsFieldsUid,
    addedLearningGoalsFields,
    fieldRefs,
    updateInputField,
    t,
    isDynamicFieldDropdown,
    fieldList,
    assessmentFields,
    isSubQuestion,
    unitPlanTemplate,
  } = props;

  const field = _.find(data, { uid: fieldUid });

  if (_.isEmpty(field) && !isDynamicFieldDropdown) return null;

  const id = _.get(field, "id", null);
  const uid = _.get(field, "uid", null);
  const value = _.get(field, "value", null);

  let fieldDetails = _.get(dynamicFieldDetails, [uid, "fieldConfig"], {});

  if (_.isEmpty(fieldDetails)) {
    fieldDetails = _.get(addedLearningGoalsFields, uid, {});
  }

  const { type, viewType, label, config } = fieldDetails;

  const isLearningGoalsRevampField = getIsLearningGoalsRevampEnabled();

  const getAdditionalProps = () => {
    const commonProps = {
      assessmentTools,
      isFromQuestion: true,
    };

    const learningGoalsAndAssessmentProps = {
      selectedCourseId,
      assessmentFields,
      templateId: worksheetTemplateId,
      testId: `worksheet-question-${uid}`,
      unitPlanFieldList,
      unitPlanFields,
      assessmentType,
    };

    const hasAddRemoveValues = _.get(
      fieldList,
      `${uid}.config.hasAddRemoveValues`,
      false
    );

    const revampedLearningGoalsProps = isLearningGoalsRevampField && {
      field_list: fieldList,
      hasAddRemoveValues,
      unitPlanTemplate,
    };

    if (uid == FIELD_UID_ASSESSMENT_TOOL) {
      return {
        ...commonProps,
        ...learningGoalsAndAssessmentProps,
        parentId: questionId,
        parentType: RESOURCE_TYPE_QUESTION,
        resourceFields: assessmentFields,
        field_list: fieldList,
      };
    } else if (uid == FIELD_UID_ADD_LEARNING_GOALS) {
      return {
        ...commonProps,
        ...learningGoalsAndAssessmentProps,
        defaultOptions: learningGoalsDropdownOptions,
        value,
        ...revampedLearningGoalsProps,
      };
    } else if (_.includes(addedLearningGoalsFieldsUid, uid)) {
      return {
        ...commonProps,
        ...learningGoalsAndAssessmentProps,
        field_list: addedLearningGoalsFields,
        subFields: getSubFields({
          fieldKey: uid,
          fieldObj: addedLearningGoalsFields[uid],
          unitPlanFields,
        }),
        ...revampedLearningGoalsProps,
      };
    } else {
      return {};
    }
  };

  const showOnlyOnActiveQuestion = _.get(
    fieldDetails,
    "showOnlyOnActiveQuestion",
    false
  );
  const showOnlyOnModes = _.get(fieldDetails, "showOnlyOnModes", []);
  const hideField = getIsFieldHidden({
    showOnlyOnActiveQuestion,
    isActiveQuestion,
    showOnlyOnModes,
    mode,
    fieldUid: uid,
    value,
    showDynamicFields,
    isSubQuestionSelected,
  });

  let fieldType = getFieldType({
    type,
    viewType,
    mode,
    isActiveQuestion,
    showDynamicFields,
    isSubQuestionSelected,
  });

  if (_.includes(addedLearningGoalsFieldsUid, uid)) {
    fieldType = viewType;
  }

  const isRemoveBtnEnabled =
    mode == MODE_TEACHER_CREATE &&
    isActiveQuestion &&
    !_.includes(addedLearningGoalsFieldsUid, uid);

  /** assessment tools are always in edit mode */
  const containerClasses = classNames(classes.container, {
    [classes.editContainer]:
      mode == MODE_TEACHER_CREATE &&
      (isActiveQuestion || uid == FIELD_UID_ASSESSMENT_TOOL),
    [classes.subContainer]: _.includes(addedLearningGoalsFieldsUid, uid),
    [classes.hideContainer]:
      (_.isEqual(fieldUid, FIELD_UID_ADD_LEARNING_GOALS) &&
        _.isEmpty(addedLearningGoalsFields)) ||
      (_.isEqual(fieldUid, FIELD_UID_ASSESSMENT_TOOL) &&
        _.isEmpty(_.get(assessmentTools, "edges"))),
  });

  return hideField ? null : (
    <div key={id} className={containerClasses}>
      <div ref={ref => (fieldRefs.current[uid] = ref)}>
        <QuestionFieldComponent
          labelComponent={
            <QuestionFieldLabel
              id={id}
              isRemoveBtnEnabled={isRemoveBtnEnabled}
              icon={_.get(dynamicFieldDetails, `${uid}.icon`, null)}
              labelLocale={_.get(dynamicFieldDetails, `${uid}.localeLabel`)}
              informationText={
                mode == MODE_TEACHER_CREATE
                  ? _.get(dynamicFieldDetails, `${uid}.informationText`)
                  : null
              }
              data={data}
              assessmentTools={assessmentTools}
              questionId={questionId}
              isFromInternalTool={isFromInternalTool}
              portalType={portalType}
              extraRightButtonComponent={getExtraRightButtonComponent(
                uid,
                _.get(config, "defaultOptions", [])
              )}
              label={label || _.get(config, "label", "")}
              learningGoalsFields={learningGoalsFields}
              mode={mode}
              isSubLabel={_.includes(addedLearningGoalsFieldsUid, uid)}
              isSubQuestion={isSubQuestion}
            />
          }
          label={t(label)}
          fieldValue={value}
          updateInputField={updateInputField}
          customTextStyle={styles.customTextStyle}
          fieldId={id}
          fieldUid={uid}
          mode={mode}
          isActiveQuestion={isActiveQuestion}
          hideField={hideField}
          setDidMount={setDidMount}
          didMount={didMount}
          portalType={portalType}
          customRef={setDynamicFieldRef}
          {...config}
          {...getAdditionalProps()}
          type={fieldType}
          learningGoalsMapping={learningGoalsMapping}
          getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
          learningGoalsFields={learningGoalsFields}
        />
      </div>
      {_.isEqual(FIELD_UID_ADD_LEARNING_GOALS, uid) ? (
        <SubDynamicFieldComponent {...props} />
      ) : null}
    </div>
  );
};

const SubDynamicFieldComponent = props => {
  const { addedLearningGoalsFieldsUid } = props;

  return _.map(addedLearningGoalsFieldsUid, fieldUid => (
    <DynamicFieldComponent {...props} fieldUid={fieldUid} />
  ));
};

export default DynamicFieldComponent;

DynamicFieldComponent.propTypes = {
  fieldUid: PropTypes.string,
  getExtraRightButtonComponent: PropTypes.func,
  data: PropTypes.array,
  isActiveQuestion: PropTypes.bool,
  questionId: PropTypes.string,
  showDynamicFields: PropTypes.bool,
  isSubQuestionSelected: PropTypes.bool,
  isFromInternalTool: PropTypes.bool,
  portalType: PropTypes.string,
  learningGoalsDropdownOptions: PropTypes.array,
  selectedCourseId: PropTypes.string,
  assessmentType: PropTypes.string,
  worksheetTemplateId: PropTypes.string,
  unitPlanFieldList: PropTypes.array,
  getLearningGoalsTagCountTooltip: PropTypes.func,
  setDynamicFieldRef: PropTypes.func,
  updateInputField: PropTypes.func,
  t: PropTypes.func,
  isDynamicFieldDropdown: PropTypes.bool,
  fieldList: PropTypes.object,
  assessmentFields: PropTypes.arrayOf(PropTypes.object),
  isSubQuestion: PropTypes.bool,
};

DynamicFieldComponent.defaultProps = {
  isDynamicFieldDropdown: false,
  fieldList: {},
  assessmentFields: [],
};
